/**
 * @module plugins/musicfavourite/adapter/favouriteApi
 *
 * Client for Music Favourite API.
 */
define(
    'plugins/musicfavourite/adapter/favouriteApi',['jquery-1.9', './favouriteItemsCache', './knownErrors', '../user', '../env', '../model/map', './idAvailability'],
    function($, Cache, Errors, User, Environment, Map, idAvailability, exports) {

        'use strict';

        var cacheLifetimeInMilliseconds = 60000;
        var cacheRequestUrl = Environment.resolveApiURL("/modules/plugin/musicfavourite/my/favourites");
        var cache = new Cache(cacheRequestUrl, cacheLifetimeInMilliseconds);

        function serialiseItem() {
            return function() {
                return {
                    'id': this.id,
                    'type': this.type,
                    'title': this.title,
                    'appId': this.appId,
                    'product': this.product,
                    'platform': this.platform,
                    'contextId': this.contextId,
                    'contextType': this.contextType,
                    'playlistType': this.playlistType,
                    'postedTime': this.postedTime
                };
            };
        }

        function getJsonString(item) {
            if (Array.isArray(item)) {
                item.forEach(function(object) {
                    object.toJSON = serialiseItem();
                });
            } else {
                item.toJSON = serialiseItem();
            }

            return JSON.stringify(item);
        }

        function returnEmptyResult() {
            return $.Deferred().resolve(new Map()).promise();
        }

        function returnError(xhrOrError, textStatus, errorThrown) {
            if (xhrOrError instanceof Error) {
                return xhrOrError;
            }

            var xhr = xhrOrError;
            var errorName = (xhr && xhr.responseText && JSON.parse(xhr.responseText).error) || 'ServiceError', error;

            if (errorName in Errors) {
                error = Errors[errorName];
            } else {
                error = new Error();
                error.name = errorName;
                error.eventDescriptor = 'service';
                error.xhr = xhr;
                error.status = xhr && xhr.status || textStatus;
                error.response = xhr && xhr.responseText && JSON.parse(xhr.responseText).message;
                error.message = error.status + ' ' + errorThrown;
            }

            return $.Deferred().reject(error).promise();
        }

        function getAllItems() {
            return cache.get();
        }

        function getItem(item) {
            var id = item.id;
            var type = (item.type === 'playlist' && item.playlistType === 'PRESENTER_PLAYLIST') ? 'presenter' : item.type;
            var url = Environment.resolveApiURL("/modules/plugin/musicfavourite/my/favourites/" + type + "/" + id);

            return idAvailability.ajaxIfIdAvailable({
                url: url,
                method: 'GET',
                dataType: 'jsonp',
                contentType: 'application/json'
            }).then(function(data) {
                return data && new Map(data);
            });
        }

        function addItem(item) {
            if (!cache.canAdd(item)) {
                return $.Deferred().reject(Errors.LimitReached);
            }

            var url = Environment.resolveApiURL("/modules/plugin/musicfavourite/my/favourites/add");
            return sendPostRequest(item, url).then(function(data) {
                cache.add(data);
                return data;
            });
        }

        function removeItem(item) {
            var url = Environment.resolveApiURL("/modules/plugin/musicfavourite/my/favourites/remove");
            return sendPostRequest(item, url).then(function(data) {
                cache.remove(data);
                return data;
            });
        }

        function addFeedback(item) {
            var url = Environment.resolveApiURL("/modules/plugin/musicfavourite/my/favourites/addfeedback");
            return sendPostRequest(item, url).then(function(data) {
                cache.add(data);
                return data;
            });
        }

        function removeFeedback(item) {
            var url = Environment.resolveApiURL("/modules/plugin/musicfavourite/my/favourites/removefeedback");
            return sendPostRequest(item, url).then(function(data) {
                cache.remove(data);
                return data;
            });
        }

        function sendPostRequest(item, url) {
            return idAvailability.ajaxIfIdAvailable({
                url: url,
                method: 'POST',
                dataType: 'json',
                contentType: 'application/json',
                data: getJsonString(item),
                xhrFields: {
                    withCredentials: true
                }
            });
        }

        function refreshTokensAndMakeApiCall(apiCall) {
            return $.Deferred(function(deferred) {
                if (User.isSignedIn()) {
                    User.refreshTokens()
                        .then(apiCall, function() { return $.Deferred().reject(Errors.TokenRefreshFailed); })
                        .then(deferred.resolve, deferred.reject);
                } else {
                    deferred.reject(Errors.NotSignedIn);
                }
            }).promise();
        }

        exports = {

            'get': function() {
                return refreshTokensAndMakeApiCall(getAllItems)
                    .then(null, returnEmptyResult);
            },

            'isFavourited': function(item) {
                return refreshTokensAndMakeApiCall(getItem.bind(null, item))
                    .then(null, returnEmptyResult);
            },

            'add': function(item) {
                return refreshTokensAndMakeApiCall(addItem.bind(null, item))
                    .then(null, returnError);
            },

            'remove': function(item) {
                return refreshTokensAndMakeApiCall(removeItem.bind(null, item))
                    .then(null, returnError);
            },

            'addFeedback': function(item) {
                return refreshTokensAndMakeApiCall(addFeedback.bind(null, item))
                    .then(null, returnError);
            },

            'removeFeedback': function(item) {
                return refreshTokensAndMakeApiCall(removeFeedback.bind(null, item))
                    .then(null, returnError);
            },

            // Allow overriding of the Cache object for stubbing
            'setCache': function(override) {
                cache = override;
            },

            // Allow overriding of the User module for stubbing
            'setUserModule': function(override) {
                User = override;
            },

            // Allow overriding of the Map model for stubbing
            'setMapModule': function(override) {
                Map = override;
            }
        };

        return exports;
    }
);

